// Bridges druif's own wine model (apps/druif/internal/model.Wine, as served
// through this app's own druif API client -- see api.ts's Wine interface)
// to @proef/contract's WineIdentity, the shape every edge (apps/edge) route
// -- tastings, cellar, flights -- actually stores. The two disagree on two
// points this module resolves, deliberately, once:
//
//  - id: druif's is a bare number, scoped to druif's own database; prefixed
//    "druif:<id>" so it can never collide with a manually-entered wine's
//    client-generated id in the edge's `wines` table.
//  - colour: druif's vocabulary (white/rose/red/sparkling/fortified/other/
//    unknown) is broader than the edge contract's (white/rose/red plus a
//    separate `sparkling` boolean) -- WINE_COLOUR_MAP below is the one
//    place that reconciles them.
import type { WineIdentity } from "@proef/contract";
import type { Wine as DruifWine } from "./api";

/** druif colour -> { contract colour, sparkling }. "fortified" reads as red
 * by default (Port/most fortified reds outnumber Sherry-style whites in
 * druif's own catalogue) and "other"/"unknown" fall back to white -- both
 * are a display simplification, never asserted as fact; the taster's own
 * note (StructuredNote.appearance.colour) is the wine's real colour of
 * record for anything actually judged in the glass. */
const WINE_COLOUR_MAP: Record<string, { colour: "white" | "rose" | "red"; sparkling: boolean }> = {
  white: { colour: "white", sparkling: false },
  rose: { colour: "rose", sparkling: false },
  red: { colour: "red", sparkling: false },
  sparkling: { colour: "white", sparkling: true },
  fortified: { colour: "red", sparkling: false },
  other: { colour: "white", sparkling: false },
  unknown: { colour: "white", sparkling: false },
};

export function druifWineId(id: number): string {
  return `druif:${id}`;
}

const DRUIF_WINE_ID = /^druif:(\d+)$/;

/** The catalogue (druif) id behind an identity id, or undefined for a wine
 * with no catalogue page: a manual entry's id is a client-generated UUID that
 * druif has never heard of, and linking it as a number is how /wine/<id>
 * once fell through to the Journal. */
export function catalogueWineId(identityId: string): number | undefined {
  const match = DRUIF_WINE_ID.exec(identityId);
  const digits = match?.[1];
  if (digits === undefined) return undefined;
  const id = Number(digits);
  return Number.isSafeInteger(id) && id > 0 ? id : undefined;
}

/** The in-app path of an identity's catalogue page, or undefined when it has none. */
export function catalogueWinePath(identityId: string): string | undefined {
  const id = catalogueWineId(identityId);
  return id === undefined ? undefined : `/wine/${String(id)}`;
}

/** A wine only the edge knows has no catalogue row, so its page is rendered from the identity. */
export function edgeWinePath(identityId: string): string {
  return `/wine/${encodeURIComponent(identityId)}`;
}

/** Where a wine's page lives: the catalogue's when it has one, else the edge-only page. */
export function winePagePath(identityId: string): string {
  return catalogueWinePath(identityId) ?? edgeWinePath(identityId);
}

const EDGE_WINE_ID =
  /^(?:manual:.+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/**
 * The identity id behind a /wine/:id segment that is not a catalogue id: `manual:<uuid>` as a
 * hand-entered wine is keyed, or a bare UUID from before that prefix. Junk stays undefined, so
 * it lands on the not-found state rather than on a fetch for nothing.
 */
export function edgeWineIdFromSegment(segment: string): string | undefined {
  let decoded: string;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    return undefined;
  }
  return EDGE_WINE_ID.test(decoded) ? decoded : undefined;
}

const ENTITY_ID = /^(?:druif:)?(\d+)$/;

/** Reads the `:id` segment of a /wine, /winery or /appellation route. A bare
 * number is the normal case; the `druif:<n>` identity form is accepted too so
 * links minted before the fix still resolve. Anything else (a manual wine's
 * UUID, junk, zero) is undefined and the route shows its not-found state. */
export function entityIdFromSegment(segment: string): number | undefined {
  let decoded = segment;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    return undefined;
  }
  const digits = ENTITY_ID.exec(decoded)?.[1];
  if (digits === undefined) return undefined;
  const id = Number(digits);
  return Number.isSafeInteger(id) && id > 0 ? id : undefined;
}

/** Converts a druif wine (as the scan/search screens already show it) into
 * the identity the edge API upserts. `vintage` picks one year from druif's
 * own vintage list (that field is plural there -- many vintages exist of
 * one wine -- but a tasting/cellar entry is always of one bottle); pass the
 * vintage the user actually confirmed (e.g. the label's own printed year),
 * falling back to the newest vintage druif has on file. */
export function toWineIdentity(wine: DruifWine, vintage?: number): WineIdentity {
  const { colour, sparkling } = WINE_COLOUR_MAP[wine.colour] ?? {
    colour: "white" as const,
    sparkling: false,
  };
  const rawCountry = wine.country ?? "";
  const country = /^[A-Za-z]{2}$/.test(rawCountry) ? rawCountry.toUpperCase() : "XX";
  const chosenVintage = vintage ?? wine.vintages?.[wine.vintages.length - 1];
  return {
    id: druifWineId(wine.id),
    producer: wine.winery !== undefined && wine.winery !== "" ? wine.winery : "Unknown producer",
    name: wine.name,
    ...(chosenVintage !== undefined ? { vintage: chosenVintage } : {}),
    grapes: wine.grapes,
    country,
    ...(wine.region !== undefined && wine.region !== "" ? { region: wine.region } : {}),
    ...(wine.appellation !== undefined && wine.appellation !== ""
      ? { appellation: wine.appellation }
      : {}),
    colour,
    sparkling,
    gtins: [],
  };
}

/**
 * The catalogue wine an edge identity stands for, for screens that render druif's shape. `undefined`
 * when the identity has no catalogue id: such a wine has no page to open, so a scan must not
 * present it as one.
 */
export function identityToWine(identity: WineIdentity): DruifWine | undefined {
  const id = catalogueWineId(identity.id);
  if (id === undefined) return undefined;
  return {
    id,
    name: identity.name,
    winery: identity.producer,
    ...(identity.region === undefined ? {} : { region: identity.region }),
    ...(identity.appellation === undefined ? {} : { appellation: identity.appellation }),
    ...(identity.country === "XX" ? {} : { country: identity.country }),
    colour: identity.sparkling ? "sparkling" : identity.colour,
    grapes: identity.grapes,
    ...(typeof identity.vintage === "number" ? { vintages: [identity.vintage] } : {}),
    products: 0,
    ratingCount: 0,
    source: "edge",
    sourceRef: identity.id,
  };
}

/** A minimal manual/unknown-bottle identity, for a blind tasting or a
 * cellar entry with no catalogue match -- every field the taster actually
 * typed, nothing invented. */
export function manualWineIdentity(input: {
  producer: string;
  name: string;
  vintage?: number;
  country: string;
  colour: "white" | "rose" | "red";
  sparkling?: boolean;
  grapes?: string[];
  region?: string;
  appellation?: string;
}): WineIdentity {
  return {
    id: `manual:${crypto.randomUUID()}`,
    producer: input.producer,
    name: input.name,
    ...(input.vintage !== undefined ? { vintage: input.vintage } : {}),
    grapes: input.grapes ?? [],
    country: /^[A-Za-z]{2}$/.test(input.country) ? input.country.toUpperCase() : "XX",
    ...(input.region !== undefined && input.region !== "" ? { region: input.region } : {}),
    ...(input.appellation !== undefined && input.appellation !== ""
      ? { appellation: input.appellation }
      : {}),
    colour: input.colour,
    sparkling: input.sparkling ?? false,
    gtins: [],
  };
}

/** True for the route segment of a hand-entered wine (`manual:<uuid>`), which has no catalogue page. */
export function isManualWineSegment(segment: string): boolean {
  try {
    return decodeURIComponent(segment).startsWith("manual:");
  } catch {
    return false;
  }
}
